/**
 * One chart request's timings, server and client, as rows a panel can draw.
 *
 * The numbers arrive from two machines and three places — the server's stage
 * list, the transport's own stamps, and the view's — and the whole job of this
 * module is to combine them **without inventing a number**. It is pure and
 * tested for the reason the rest of `lib/` is: everything that touches the
 * chart is out of jsdom's reach, so the arithmetic has to live somewhere a test
 * can reach it.
 *
 * ## The three things that are easy to get wrong here
 *
 * **The wire is not measurable on a refresh.** The client stamps `wireMs` from
 * its last `send`, and the 60-second refresh is a push it never asked for — so
 * on a refresh that number is *the age of the subscription*, up to a minute,
 * and printing it as network latency would be the most confidently wrong thing
 * on the panel. `source: "refresh"` therefore reports no wire estimate at all.
 *
 * **The wire estimate is a subtraction of two durations, not of two clocks.**
 * `wireMs` is measured entirely on the client and `totalMs` entirely on the
 * server, so the difference needs no agreement between the two machines about
 * what time it is. It is still an *estimate* — it holds queueing, the socket
 * write, and the server's own dispatch either side of the build — and it is
 * labelled as one everywhere it appears.
 *
 * **What the request waited through before the build is the server's, not
 * the wire's.** The server reports it as `beforeBuild` — the socket's sign-in
 * on its first request, then the wait for a worker thread — and it is taken
 * out of the wire estimate and shown as server rows ahead of the build. Before
 * it was reported, it *was* the wire: on 2026-09-25 a chart read "wire ≈ 3.1 s"
 * on 851 KB whose bytes took ~0.1 s, and the 3.1 s was a sign-in check against
 * a slow Postgres. A server that does not send it leaves the estimate — and its
 * label — as they were, because then the wire genuinely still contains it.
 *
 * **Unattributed is a finding, not an error.** The server sends the outer
 * bracket rather than the sum of its stages, so `total − Σstages` is whatever
 * nobody wrapped. Showing it is the point: a stage that is never named is a
 * stage nobody will ever optimise.
 */

import type { ServerPerf } from "@qufolio/transport";

/** What the client spent, once the bytes were in hand. */
export interface ClientTiming {
  /** `ws.send` → first byte. Contains the server's whole build. */
  wireMs: number;
  /**
   * Envelope framing + the wire format decoded into plain arrays. Paid once per
   * bundle.
   *
   * **The rows name no library** (owner's call, 2026-09-19). A reader looking
   * at their own chart has no use for which decoder or renderer is underneath,
   * and nothing else on screen names a dependency.
   */
  decodeMs: number;
  /** `diffChartGroup`. Zero on the first load, which does not diff. */
  diffMs: number;
  /** Columns re-keyed onto plots (`bulkDeltas` / `appendDeltas`). */
  mapMs: number;
  /** The append into the renderer's own series — a per-point loop. */
  bindMs: number;
  /** Bar timestamps onto the axis and the annotations. */
  barTimesMs: number;
  /** The inspector's own column copy. Its own cost, shown as its own. */
  snapshotMs: number;
  /** Bytes on the wire, whole bundle. */
  bytes: number;
  /** How many chart groups shared this response. */
  groupCount: number;
}

/** One landed frame, kept so the panel can show the open and the latest. */
export interface ChartTimingRecord {
  /** Wall clock when it landed, for "3 minutes ago". */
  at: number;
  /** `open` is the first bulk load — the cost a reader actually felt. */
  kind: "open" | "update";
  /** Absent when the server sent no timings: an older build, or a fixture. */
  server?: ServerPerf;
  client: ClientTiming;
}

/** Both records a chart keeps. Either may be absent before the first load. */
export interface ChartTimings {
  open?: ChartTimingRecord;
  latest?: ChartTimingRecord;
}

export type TimingSide = "server" | "wire" | "client" | "unattributed";

export interface TimingRow {
  name: string;
  ms: number;
  detail?: string;
  side: TimingSide;
  /** Share of {@link TimingReading.totalMs}, 0..1 — for the proportional bar. */
  fraction: number;
}

export interface TimingReading {
  rows: TimingRow[];
  /** **This chart's** server bracket — its own read plus its own build. */
  serverMs: number;
  /**
   * What the request waited through in the server **before** the build began —
   * sign-in, then the wait for a worker. Zero when the server did not report
   * it, and on a refresh. Outside {@link serverMs}, so the build number keeps
   * its meaning.
   */
  beforeBuildMs: number;
  /** The rows that make up {@link beforeBuildMs}, for the header's breakdown. */
  beforeBuild: TimingRow[];
  /**
   * The whole response the server built, every chart in it.
   *
   * Shown beside {@link serverMs} only when a layout put more than one chart on
   * the socket, where the gap between them is the other charts' work and is
   * worth naming. Charging it to this chart is the error the two fields exist
   * to keep apart.
   */
  bundleMs: number;
  /** Everything the client did after the bytes landed. */
  clientMs: number;
  /**
   * Transport, **estimated** — plus the server's pre-build wait when an older
   * server did not report it. `null` on a refresh, where the client has nothing
   * honest to measure it from.
   */
  wireMs: number | null;
  /** `serverMs − Σ server stages`. Zero when nothing is missing. */
  unattributedMs: number;
  /** What the reader waited for, as far as any of this can say. */
  totalMs: number;
  source?: "request" | "refresh";
  bytes: number;
  groupCount: number;
}

/** Never negative, never NaN — a panel row is not the place to debate a float. */
function clamp(ms: number): number {
  return Number.isFinite(ms) && ms > 0 ? ms : 0;
}

/**
 * The rows for one record, in pipeline order: server, wire, then client.
 *
 * Order is the reading, exactly as it is on the server — this is the sequence
 * the request actually went through, and sorting it by duration would put the
 * answer to "what is slow" in front of the answer to "what happens", which is
 * the question a first-time reader has.
 */
export function readTiming(record: ChartTimingRecord): TimingReading {
  const { server, client } = record;

  const serverStages: TimingRow[] = (server?.stages ?? []).map((stage) => ({
    name: stage.name,
    ms: clamp(stage.ms),
    detail: stage.detail,
    side: "server" as const,
    fraction: 0,
  }));

  const serverMs = clamp(server?.totalMs ?? 0);
  const stagesSum = serverStages.reduce((n, row) => n + row.ms, 0);
  // Only ever a positive remainder. A stage list that somehow exceeded its own
  // bracket is a bug in the accumulator, and a negative slice on a bar would
  // report it as a negative *duration*, which is not what happened.
  const unattributedMs = clamp(serverMs - stagesSum);

  const bundleMs = clamp(server?.bundleTotalMs ?? serverMs);

  // **Never on a refresh**, for the reason the wire is not: a push waited for
  // nothing this reader asked for. The server sends none then anyway; this says
  // so where the rule lives rather than trusting the payload's silence.
  const beforeBuild: TimingRow[] =
    server?.source === "refresh"
      ? []
      : (server?.beforeBuild ?? []).map((stage) => ({
          name: stage.name,
          ms: clamp(stage.ms),
          detail: stage.detail,
          side: "server" as const,
          fraction: 0,
        }));
  const beforeBuildMs = beforeBuild.reduce((n, row) => n + row.ms, 0);
  const waitReported = beforeBuild.length > 0;

  // **Not computed on a refresh.** See the module note: the client's stamp then
  // measures the age of the subscription, not the network.
  //
  // Subtracted against the **bundle**, not this chart's share: the round trip
  // waited for the whole response, so on a two-chart layout taking only this
  // chart's build would bill the other chart's work to the network.
  //
  // The pre-build wait comes out too: the client's stamp spans it, and it is
  // the server's, measured and shown as its own rows.
  const wireMs =
    server?.source === "refresh"
      ? null
      : clamp(clamp(client.wireMs) - bundleMs - beforeBuildMs);

  const clientRows: TimingRow[] = [
    { name: "decode", ms: clamp(client.decodeMs), detail: "wire format → arrays" },
    { name: "diff", ms: clamp(client.diffMs), detail: "against the last snapshot" },
    { name: "map", ms: clamp(client.mapMs), detail: "columns → plot series" },
    { name: "bind", ms: clamp(client.bindMs), detail: "append into the chart" },
    { name: "bar times", ms: clamp(client.barTimesMs), detail: "axis + annotations" },
    { name: "inspector copy", ms: clamp(client.snapshotMs), detail: "this panel's own cost" },
  ].map((row) => ({ ...row, side: "client" as const, fraction: 0 }));

  const clientMs = clientRows.reduce((n, row) => n + row.ms, 0);

  const rows: TimingRow[] = [
    ...beforeBuild,
    ...serverStages,
    ...(unattributedMs > 0
      ? [
          {
            name: "unattributed",
            ms: unattributedMs,
            detail: "inside the server build, not yet named by a stage",
            side: "unattributed" as const,
            fraction: 0,
          },
        ]
      : []),
    ...(wireMs === null
      ? []
      : [
          {
            name: "wire",
            ms: wireMs,
            // The old label only while the wire still holds the server's wait.
            detail: waitReported
              ? "estimated — network, both directions"
              : "estimated — transport and queueing, both directions",
            side: "wire" as const,
            fraction: 0,
          },
        ]),
    ...clientRows,
  ];

  const totalMs = beforeBuildMs + serverMs + (wireMs ?? 0) + clientMs;
  // Guarded rather than assumed non-zero: a fixture transport reports every
  // stage at 0, and a bar of NaN-width segments renders as nothing at all with
  // no hint as to why.
  for (const row of rows) row.fraction = totalMs > 0 ? row.ms / totalMs : 0;

  return {
    rows,
    serverMs,
    beforeBuildMs,
    beforeBuild,
    bundleMs,
    clientMs,
    wireMs,
    unattributedMs,
    totalMs,
    source: server?.source,
    bytes: client.bytes,
    groupCount: client.groupCount,
  };
}

/**
 * A duration, at the precision it is worth reading.
 *
 * Sub-millisecond spans are real here — `resample` on a daily chart is the
 * identity — so they round to `0.0` rather than disappearing into `0`, which
 * would read as "did not run" beside stages that genuinely did not.
 */
export function formatMs(ms: number): string {
  if (!Number.isFinite(ms)) return "—";
  if (ms >= 10_000) return `${(ms / 1000).toFixed(1)} s`;
  if (ms >= 100) return `${Math.round(ms)} ms`;
  return `${ms.toFixed(1)} ms`;
}

/** `421888` → `412 KB`. Bytes are only ever read as a magnitude here. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "—";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/** "just now" / "12s ago" / "3m ago" — the age of a reading, for the header. */
export function formatAge(at: number, now: number): string {
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 2) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  return `${Math.round(seconds / 60)}m ago`;
}
